import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useAgentContext } from '../../context/agent';
import { useMutation, useQueryClient } from 'react-query';
import { addTextBlock, updateTextBlock } from '../../api/agent';
import { FaFileAlt, FaPlus, FaTimes, FaExclamationTriangle, FaEdit } from 'react-icons/fa';
import { ITextBlock } from '../../models/ITextblock';

type Props = {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    textBlock?: ITextBlock;
}

const ManageTextBlock = ({show, setShow, textBlock} : Props) => {
    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const [error, setError] = useState<string | null>(null);
    
    const isEditMode = !!textBlock;

    const {agentId} = useParams();
    const {getAgentById} = useAgentContext();
    const agent = getAgentById(agentId || "");

    // Initialize form fields when text block is provided for editing
    useEffect(() => {
        if (textBlock) {
            setTitle(textBlock.title);
            setText(textBlock.text);
        } else {
            setTitle("");
            setText("");
        }
        setError(null);
    }, [textBlock]);

    const handleClose = () => {
        setShow(false);
        setError(null);
        if (!isEditMode) {
            setTitle("");
            setText("");
        }
    };

    const queryClient = useQueryClient();
    
    const { mutateAsync: addTextBlockFn, isLoading: isAdding } = useMutation(
        addTextBlock,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['TextBlocks', agent?.id]);
                handleClose();
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.message || "Failed to add text block. Please try again.";
                setError(errorMessage);
            },
        }
    );

    const { mutateAsync: updateTextBlockFn, isLoading: isUpdating } = useMutation(
        updateTextBlock,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['TextBlocks', agent?.id]);
                handleClose();
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.message || "Failed to update text block. Please try again.";
                setError(errorMessage);
            },
        }
    );

    const handleSubmit = async () => {
        if(!text?.trim()) {
            setError('Content is required.');
            return;
        }
        
        if(!agent) {
            setError("Unexpected error. Please reload page and retry.");
            return;
        }

        try {
            if (isEditMode && textBlock) {
                await updateTextBlockFn({
                    orgId: agent.organizationId,
                    agentId: agent.id,
                    id: textBlock.id,
                    title: title.trim(),
                    text: text.trim(),
                });
            } else {
                await addTextBlockFn({
                    agentId: agent.id,
                    orgId: agent.organizationId,
                    title: title.trim(),
                    text: text.trim(),
                });
            }
        } catch (err) {
            // Error handling is done in the mutation's onError callback
        }
    };

    const isLoading = isAdding || isUpdating;

    return (
        <Modal 
            show={show} 
            onHide={handleClose}
            size="lg"
            contentClassName="bg-dark border-secondary"
        >
            <Modal.Header 
                closeButton
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <div className="d-flex align-items-center">
                    <div 
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#007bff",
                            borderRadius: "8px"
                        }}
                    >
                        <FaFileAlt className="text-white" size={16} />
                    </div>
                    <Modal.Title className="text-light mb-0" style={{ fontSize: "18px", fontWeight: "600" }}>
                        {isEditMode ? "Edit Text Block" : "Add Text Block"}
                    </Modal.Title>
                </div>
            </Modal.Header>
            
            <Modal.Body style={{ backgroundColor: "#1a1a1a" }}>
                <Form>
                    <Form.Group className="mb-4">
                        <Form.Label className="text-light fw-semibold mb-2">
                            Title
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Enter a descriptive title for this text block.
                        </Form.Text>
                        <Form.Control
                            id='title'
                            type='text'
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="bg-dark text-light border-secondary mb-4"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff"
                            }}
                            placeholder="Company Policy Guidelines"
                            disabled={isLoading}
                        />
                        
                        <Form.Label className="text-light fw-semibold mb-2">
                            Content
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Enter the text content that will be used to train your agent.
                        </Form.Text>
                        <Form.Control
                            id='content'
                            as="textarea" 
                            rows={8}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className="bg-dark text-light border-secondary"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff",
                                resize: "vertical"
                            }}
                            placeholder="Our company values customer satisfaction above all else. We provide 24/7 support and guarantee..."
                            disabled={isLoading}
                        />
                    </Form.Group>
                </Form>
                
                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: "#2d1a1a",
                            borderColor: "#dc3545",
                            color: "#ff6b6b"
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FaExclamationTriangle className="me-2" />
                            {error}
                        </div>
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    className="d-flex align-items-center"
                    style={{ borderColor: "#555", color: "#ccc" }}
                    disabled={isLoading}
                >
                    <FaTimes className="me-2" size={14} />
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={isLoading || !text?.trim()}
                    className="d-flex align-items-center"
                    style={{ 
                        backgroundColor: "#007bff",
                        borderColor: "#007bff"
                    }}
                >
                    {isLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            {isEditMode ? "Updating..." : "Adding..."}
                        </>
                    ) : (
                        <>
                            {isEditMode ? (
                                <FaEdit className="me-2" size={14} />
                            ) : (
                                <FaPlus className="me-2" size={14} />
                            )}
                            {isEditMode ? "Update Text Block" : "Add Text Block"}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ManageTextBlock;